import React from 'react';
import { Box, Select, HStack, VStack, } from 'native-base';
import { faAngleRight, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { SpIcon } from '~/shared/Icon';
import { Caption } from '~/shared/Typography';
import { AppRouteProps, useAppNavigate } from '~/pages';
import { useEffectOnce } from 'usehooks-ts';
import {
    I18nReacher,
    Member,
    useAppSettings,
    useI18n,
    useSpringBigStore,
    useTheme,
} from '@springbig/shared';
import { Panel, Button } from '~/shared/Theme';
import DefaultLang from '@springbig/shared/src/i18n/defaults';
import DefaultLangES from '@springbig/shared/src/i18n/defaults_es';
import DefaultLangFR from '@springbig/shared/src/i18n/defaults_fr';
import lodashMerge from 'lodash/merge';

type ProfileMenuRoutesProps = {
    title: I18nReacher;
    path?: AppRouteProps;
    url?: string;
};

const profileMenuRoutes: (wallet_id: string, member: Member) =>
    ProfileMenuRoutesProps[] = (wallet_id: string, member: Member) => [
        {
            title: 'profileMenu.my_information',
            path: `w/${wallet_id}/profile/menu/account`,
        },
        {
            title: 'profileMenu.message_notifications',
            path: `w/${wallet_id}/profile/menu/settings`,
        },
        {
            title: 'profileMenu.terms_condition',
            url: member?.terms_and_conditions_link
        },
        {
            title: 'profileMenu.privacy_policy',
            url: member?.privacy_policy_url
        }
    ];

const ProfileMenuOptions = () => {

    const { navigate, params } = useAppNavigate();
    const { languages, englishOverride, spanishOverride } = useAppSettings();
    const { setLanguage, languageLabel, setLanguageLabel } = useI18n();
    const { member } = useSpringBigStore();

    const {
        primary,
        field_background,
        field_color,
        field_border,
        field_border_size,
        radius_sm,
    } = useTheme();

    useEffectOnce(() => {

        if (languages && languages.length > 0) {

            const currentLang = languages.find(lang => lang.label === languageLabel);

            if (currentLang) {
                return;
            }

            languages.map(lang => {

                if (lang.default) {

                    setLanguageLabel(lang.label);

                    switch (lang.label) {
                    case 'English (US)':
                        setLanguage(lang.label, lodashMerge({}, DefaultLang, englishOverride));
                        break;
                    case 'French (FR)':
                        setLanguage(lang.label, lodashMerge({}, DefaultLangFR, englishOverride));
                        break;
                    case 'Spanish (ES)':
                        setLanguage(lang.label, lodashMerge({}, DefaultLangES, spanishOverride));
                        break;
                    }
                }
            });
        }
    });

    const handleLanguageChange = (lang: string) => {

        if (languageLabel === lang) {
            return;
        }

        if (!languages) {
            return;
        }

        const _lang = languages.find((l) => l.label === lang);

        switch (_lang.label) {
        case 'English (US)':
            setLanguage(_lang.label, lodashMerge({}, DefaultLang, englishOverride));
            break;
        case 'French (FR)':
            setLanguage(_lang.label, lodashMerge({}, DefaultLangFR, englishOverride));
            break;
        case 'Spanish (ES)':
            setLanguage(_lang.label, lodashMerge({}, DefaultLangES, spanishOverride));
            break;
        }

        setLanguageLabel(lang);
    };

    return (
        <Panel space={8}>
            {
                profileMenuRoutes(params.wallet_id, member).map((route: ProfileMenuRoutesProps, index: number) => (

                    (route.url || route.path) &&
                        <Button
                            key={`${route.title}-${route.url || route.path}-${index}`}
                            modifier={'unlocked'}
                            variant={'text'}
                            onPress={() => (route.url ? window.open(route.url, '_blank', 'noopener,noreferrer') : navigate(route.path))}
                        >
                            <HStack justifyContent={'space-between'} alignItems={'center'}>
                                <Caption
                                    text={route.title}
                                    underline={route.url && true}
                                />
                                <SpIcon
                                    color={primary}
                                    icon={faAngleRight}
                                />
                            </HStack>
                        </Button>
                ))
            }
            {
                languageLabel && languages && languages.length > 1 && (
                    <VStack>
                        <Caption text={'general.select_language'} />
                        <Select
                            backgroundColor={field_background}
                            borderColor={field_border}
                            borderWidth={field_border_size}
                            color={field_color}
                            rounded={radius_sm}
                            dropdownCloseIcon={
                                <Box px={2}>
                                    <SpIcon
                                        color={primary}
                                        icon={faAngleDown}
                                    />
                                </Box>
                            }
                            selectedValue={languageLabel}
                            minWidth={90}
                            onValueChange={(itemValue) => {

                                handleLanguageChange(itemValue);
                            }}
                        >
                            {
                                languages.map((lang, index) => (

                                    <Select.Item
                                        key={`${lang.label}-${index}`}
                                        label={lang.label}
                                        value={lang.label}
                                    />
                                ))
                            }
                        </Select>
                    </VStack>

                )
            }
        </Panel>
    );

};

export default ProfileMenuOptions;
